/**
 * Formatting Utilities
 * 
 * Functions for formatting data for display
 */

/**
 * Format currency amount
 * 
 * @param amountInCents - Amount in cents
 * @param currency - Currency code (default: USD)
 * @param locale - Locale for formatting (default: en-US)
 * @returns Formatted currency string
 */
export function formatCurrency(
  amountInCents: number,
  currency: string = 'USD',
  locale: string = 'en-US'
): string {
  const amountInDollars = amountInCents / 100;

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amountInDollars);
}

/**
 * Format date
 * 
 * @param date - Date to format
 * @param format - Format type ('short' | 'long' | 'full')
 * @param locale - Locale for formatting (default: en-US)
 * @returns Formatted date string
 */
export function formatDate(
  date: Date | string | number,
  format: 'short' | 'long' | 'full' = 'short',
  locale: string = 'en-US'
): string {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;

  let options: Intl.DateTimeFormatOptions;
  
  if (format === 'short') {
    options = { month: 'numeric' as const, day: 'numeric' as const, year: 'numeric' as const };
  } else if (format === 'long') {
    options = { month: 'long' as const, day: 'numeric' as const, year: 'numeric' as const };
  } else {
    options = { weekday: 'long' as const, month: 'long' as const, day: 'numeric' as const, year: 'numeric' as const };
  }

  return new Intl.DateTimeFormat(locale, options).format(dateObj);
}

/**
 * Format date and time
 * 
 * @param date - Date to format
 * @param locale - Locale for formatting (default: en-US)
 * @returns Formatted date and time string
 */
export function formatDateTime(
  date: Date | string | number,
  locale: string = 'en-US'
): string {
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;

  return new Intl.DateTimeFormat(locale, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }).format(dateObj);
}

/**
 * Format phone number
 * 
 * @param phone - Phone number string
 * @returns Formatted phone number
 */
export function formatPhoneNumber(phone: string): string {
  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '');

  // Format as (XXX) XXX-XXXX
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  // Return as-is if not 10 digits
  return phone;
}

/**
 * Truncate text with ellipsis
 * 
 * @param text - Text to truncate
 * @param maxLength - Maximum length before truncation
 * @returns Truncated text
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) {
    return text;
  }

  return `${text.slice(0, maxLength)}...`;
}

/**
 * Convert cents to dollars
 * 
 * @param cents - Amount in cents
 * @returns Amount in dollars
 */
export function centsToDollars(cents: number): number {
  return cents / 100;
}

/**
 * Convert dollars to cents
 * 
 * @param dollars - Amount in dollars
 * @returns Amount in cents
 */
export function dollarsToCents(dollars: number): number {
  return Math.round(dollars * 100);
}
